import { useEffect, useMemo } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';

import { SessionProvider } from '../features/account/web/session-context';
import { clientConfig } from '../shared/web/config/client-config';
import { captureClientError, initClientObservability } from '../shared/web/observability/init';
import { ThemeProvider } from '../shared/web/theme/theme-context';
import { THEME_BOOTSTRAP_SCRIPT } from '../shared/web/theme/theme';

import { NativeChrome } from './native-chrome';
import { appSession } from './session';

import appStylesHref from './app.css?url';
import brandFaceHref from '../shared/web/theme/fonts/nunito-latin.woff2?url';
import labelFaceHref from '../shared/web/theme/fonts/montserrat-600-latin.woff2?url';

import type { Route } from './+types/root';
import { RestoreScrollAfterLoad } from '../shared/web/shell/restore-scroll';
import { WORDMARK, Wordmark } from '../shared/web/shell/wordmark';

export function links(): {
  rel: string;
  href: string;
  as?: string;
  type?: string;
  crossOrigin?: 'anonymous';
}[] {
  // The brand faces are preloaded so the first paint on web does not swap
  // them in a beat later (HOM-104, Proposal 6). Fonts must be fetched
  // anonymously even from the same origin, or the preload is discarded and
  // fetched twice.
  const font = (href: string) => ({
    rel: 'preload',
    href,
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous' as const,
  });
  return [{ rel: 'stylesheet', href: appStylesHref }, font(brandFaceHref), font(labelFaceHref)];
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    /*
     * `suppressHydrationWarning` because the inline script below deliberately
     * stamps `data-theme` on this element before React hydrates — that is the
     * whole point of it running pre-paint. React would otherwise warn on every
     * cold start with an explicit theme stored, and a console that cries wolf
     * on a known-good difference is a console nobody reads.
     */
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="utf-8" />
        {/*
         * `overlays-content`: the keyboard shrinks the visual viewport and leaves
         * the layout viewport alone, on Android Chrome as on iOS, so a bottom
         * sheet keeps its height and only its foot rises (shared/web/sheet/keyboard.ts).
         */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, interactive-widget=overlays-content"
        />
        {/*
         * Runs before the first paint. Without it a user who has explicitly
         * chosen dark under a light OS gets a white flash on every Capacitor
         * cold start — the one platform where the app most wants to feel native.
         * It has to be inline for the same reason: a module would arrive too late.
         */}
        <script dangerouslySetInnerHTML={{ __html: THEME_BOOTSTRAP_SCRIPT }} />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <RestoreScrollAfterLoad />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  // The one session (HOM-61), provided above everything so the sign-in
  // screen outside the house scope and the sign-out control inside it read
  // the same one. Built on first render rather than at import: the library
  // reaches for storage when constructed, and this module graph is evaluated
  // in Node at build time.
  const session = useMemo(() => appSession(), []);

  /*
   * HOM-20 built the reporting module and deliberately left it unmounted,
   * because mounting it is one call in `app/` and `app/` belonged to HOM-10.
   * This is that call — and until it existed, a module with no caller reported
   * nothing, which is indistinguishable from software that never fails.
   *
   * **In an effect, never at module scope.** `ssr: false` does not mean nothing
   * renders on a server: the build renders this route's module graph in Node to
   * produce `index.html`. `Sentry.init` reaches for browser globals, so calling
   * it while a module is being imported would fail the BUILD. The import itself
   * is fine — the SDK is import-safe outside a browser — and `npm run build:web`
   * in the check suite is what keeps that true rather than assumed.
   *
   * Configuration is passed explicitly because the module reads nothing
   * ambient; how it reaches the browser is this file's problem, not that one's.
   * Nothing here is native-specific and nothing needs to be: iOS and Android
   * load this same bundle, so a crash that shows as a blank webview reports
   * through exactly this path.
   */
  useEffect(() => {
    initClientObservability({
      dsn: clientConfig.sentryDsn,
      environment: clientConfig.environment,
      release: clientConfig.release,
      // A report the tracker's host refuses goes through the API instead,
      // once there is a token to send it with.
      relay: { url: `${clientConfig.apiOrigin}/client-reports`, getToken: session.getToken },
    });
  }, [session]);

  return (
    <SessionProvider session={session}>
      <ThemeProvider>
        <NativeChrome />
        <Outlet />
      </ThemeProvider>
    </SessionProvider>
  );
}

/**
 * What is on screen while the first loaders run.
 *
 * In SPA mode nothing beneath the root renders until the initial client
 * loaders settle, and the session gate and the house loaders both wait on
 * the network. Without this the wait is a blank page, indistinguishable from
 * a broken one. Static markup only: the build renders it into `index.html`.
 */
export function HydrateFallback() {
  return (
    <main className="content boot">
      <p className="boot__mark" role="status" aria-label={`Opening ${WORDMARK}…`}>
        <Wordmark />
      </p>
    </main>
  );
}

/**
 * The last boundary before a blank screen.
 *
 * A boundary swallows the error by design, so without this the worst failure —
 * the app rendering nothing at all — would be the one failure that reported
 * nothing at all. That is the case HOM-20's `captureClientError` exists for,
 * and on a phone it is the whole difference between a bug report and a user
 * who quietly stops opening the app.
 *
 * Rendered inside `Layout`, so the theme bootstrap has already run and this is
 * drawn in the user's theme rather than in a default one.
 */
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  useEffect(() => {
    captureClientError(error);
  }, [error]);

  return (
    <main className="content">
      <p className="placeholder">
        Something went wrong. Reopening the app should recover it — the failure has been reported.
      </p>
    </main>
  );
}
